import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  type Row,
  type HeaderGroup,
} from '@tanstack/react-table';
import { useEffect, useState, useCallback, useMemo, memo, useRef } from 'react';
import { useClickAway } from 'react-use';
import { ValueOf } from 'type-fest';

import type { BOMRow, ExtractedTableType, TableRow } from '@frontend/api';
import { ExtractedTableJson } from '@frontend/api/src/parts/useGetExtractedTables';
import {
  Button,
  Center,
  EmptyDrawerIcon,
  ErrorDescription,
  ErrorHeadline,
  Error,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Segment,
  SegmentControl,
  ModalCloseButton,
  Box,
  TableContainer,
  useToast,
  TableV2,
  Thead,
  Tr,
  Tbody,
  Tooltip,
  Flex,
  CheckmarkIcon,
  SwapVerticalIcon,
  IconButton,
} from '@frontend/components';
import { fullstoryEvent } from '@frontend/services';
import { useCopyToClipboard, useBooleanTimeout } from '@frontend/utils';

import { TABLE_TYPES } from 'modules/extractedTables/constants';
import { useExtractedTableConfig } from 'modules/extractedTables/hooks/useExtractedTableConfig';
import {
  SpreadsheetSelectionCell,
  useSpreadsheetSelection,
  buildVisibleRowNumbers,
} from 'modules/quotes/quoteItemComponents/bomBuilder/useSpreadsheetSelection';
import {
  getCellsInRectangularRange,
  generateCopyPasteText,
} from 'modules/quotes/quoteItemComponents/bomBuilder/useSpreadsheetSelection/utils';

import { NoBomInfoPopover } from './NoBomInfoPopover';
import {
  buildCssForHeaderCells,
  buildCssForSelectedCells,
  defaultColumn,
} from './cells';
import {
  countBOMRows,
  getSemanticSelectionRange,
  getCellFromSpreadsheetCell,
} from './utils';

const copyFromExtractedTableModalEvent = 'copyFromExtractedTableModal' as const;

const ExtractedTableRow = memo(({ row }: { row: Row<TableRow> }) => {
  return (
    <Tr>
      {row.getVisibleCells().map((cell) =>
        flexRender(cell.column.columnDef.cell, {
          ...cell.getContext(),
          key: cell.id,
        }),
      )}
    </Tr>
  );
});
ExtractedTableRow.displayName = 'MemoizedExtractedTableRow';

const ExtractedTableHeaderRow = memo(
  ({ headerGroup }: { headerGroup: HeaderGroup<TableRow> }) => {
    return (
      <Tr key={headerGroup.id}>
        {headerGroup.headers.map((header) =>
          flexRender(header.column.columnDef.header, {
            ...header.getContext(),
            key: header.id,
          }),
        )}
      </Tr>
    );
  },
);
ExtractedTableHeaderRow.displayName = 'MemoizedExtractedTableHeaderRow';

type ExtractedTableModalProps = {
  rawTableData: ExtractedTableJson | null;
  isLoading: boolean;
  tableJsonData: ExtractedTableType | null;
  hasTableDataError: boolean;
  isExtractedTableModalOpen: boolean;
  onCloseExtractedTableModal: () => void;
  fileLink?: React.ReactNode;
};

const emptyTableJsonData: ExtractedTableType = {
  table: {
    boundingBox: null,
    spreadsheetBoundingBox: null,
    rows: [],
    headerRows: [],
    titleRows: [],
    pageNumber: null,
    confidence: null,
  },
  bomHeader: null,
  bomRows: null,
  backupRootIdentifier: null,
};

export const ExtractedTableModal = ({
  tableJsonData,
  rawTableData,
  isLoading,
  hasTableDataError,
  isExtractedTableModalOpen,
  onCloseExtractedTableModal,
  fileLink,
}: ExtractedTableModalProps) => {
  const [tableType, setActiveTableType] = useState<ValueOf<typeof TABLE_TYPES>>(
    TABLE_TYPES.RAW,
  );
  const showBom =
    !!rawTableData &&
    rawTableData?.hasBom &&
    !rawTableData?.hasMissingQuantity &&
    !rawTableData?.hasInvalidQuantity &&
    !rawTableData?.hasInconsistentPartNumberIdentifier &&
    !rawTableData?.hasInconsistentDescriptionIdentifier;

  // handle setting table type to BOM if we have one from the extracted table
  useEffect(() => {
    setActiveTableType(showBom ? TABLE_TYPES.BOM : TABLE_TYPES.RAW);
  }, [showBom]);

  // When both bom and table, use full width
  const modalWidth = showBom ? '100%' : undefined;

  // When only table, fit the modal content to the table contents
  const modalContentWidth = !showBom ? 'fit-content' : undefined;
  const tableMinWidth = !showBom ? '450px' : undefined;

  const shouldShowControl = showBom && !isLoading;

  const toast = useToast();

  const extractedTable = tableJsonData ?? emptyTableJsonData;

  const [reverseTableRows, setReverseTableRows] = useState(false);

  const processedTableData = useMemo(() => {
    // If we're not in raw table type or we haven't reversed the rows, return the extracted table
    if (tableType !== TABLE_TYPES.RAW || !reverseTableRows)
      return extractedTable;

    // Otherwise, reverse the rows and return the processed table data
    const reversedRows = extractedTable.table.rows.slice();
    reversedRows.reverse();
    return {
      ...extractedTable,
      table: {
        ...extractedTable.table,
        rows: reversedRows,
      },
    };
  }, [tableType, extractedTable, reverseTableRows]);

  // Generate column config and data
  const { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId } =
    useExtractedTableConfig({
      extractedTable: processedTableData,
    });

  // Depending on the table type, use the appropriate columns and data
  const columns = useMemo(
    () => (tableType === TABLE_TYPES.RAW ? rawColumns : bomColumns),
    [tableType, rawColumns, bomColumns],
  );
  const data = useMemo(
    () => (tableType === TABLE_TYPES.RAW ? rawData : bomData),
    [tableType, rawData, bomData],
  );

  // Derive the number of rows in the table based on the table type
  const numRows = useMemo(
    () =>
      tableType === TABLE_TYPES.BOM
        ? countBOMRows(data as BOMRow[])
        : data.length,
    [tableType, data],
  );

  // Derive the number of countable columns based on the table type for selection purposes. BOM tables
  // do not allow selection of itemNumber, so we subtract 1 from the number of columns.
  // NB: This is not meant to be passed into useSpreadsheetSelection, which we would eventually like to
  // pull numberOfRows and numberOfColumns out as props anyway
  const numColumns = useMemo(
    () => (tableType === TABLE_TYPES.BOM ? columns.length - 1 : columns.length),
    [tableType, columns.length],
  );

  // Derive the starting column for useSpreadsheetSelection. BOM tables do not allow selection of itemNumber,
  // so we start at column 1 for spreadsheet selection.
  const startColumn = useMemo(
    () => (tableType === TABLE_TYPES.RAW ? 0 : 1),
    [tableType],
  );

  // External state for useSpreadsheetSelection
  const [selectedCell, setSelectedCell] =
    useState<SpreadsheetSelectionCell | null>(null);
  const [extentCell, setExtentCell] = useState<SpreadsheetSelectionCell | null>(
    null,
  );

  const [showTooltip, startTooltipTimeout] = useBooleanTimeout(1500);

  const onCopySuccess = useCallback(() => {
    startTooltipTimeout();

    // Create strings to append to the selection info to tell us the start and end of the selection
    const startCell = selectedCell ?? { row: 0, column: 0 };
    const startCellText = `(${startCell.row}, ${startCell.column})`;
    const endCell = extentCell ?? startCell;
    const endCellText = `(${endCell.row}, ${endCell.column})`;

    // Get the semantic selection range
    const selectionRange = getSemanticSelectionRange({
      selectedCell,
      extentCell,
      numRows,
      numColumns,
    });

    // Create the fullstory payload
    const payload = {
      tableType,
      selectionRange,
      startCell: startCellText,
      endCell: endCellText,
    };

    // Emit payload to a fullstory event
    fullstoryEvent(copyFromExtractedTableModalEvent, payload);
  }, [
    startTooltipTimeout,
    selectedCell,
    extentCell,
    tableType,
    numColumns,
    numRows,
  ]);

  const onCopyError = useCallback(() => {
    // Show a toast
    toast({ title: 'Failed to copy to clipboard', status: 'error' });
  }, [toast]);

  // Set up copy behavior / error handling
  const onAttemptCopy = useCopyToClipboard({
    onSuccess: onCopySuccess,
    onFailure: onCopyError,
  });

  const {
    gridOnKeydown: spreadsheetSelectionGridOnKeydown,
    bodyCellSelected,
    bodyCellOnInteract,
    bodyCellOnMouseEnter,
    getRowOrHeaderCellState,
    headerCellOnClick,
    rangeSelectedCells,
    setRangeSelectedCells,
  } = useSpreadsheetSelection({
    numberOfRows: numRows,
    numberOfColumns: columns.length,
    onAttemptCopy,
    startColumn,
    // Controlled state
    selectedCell,
    onSetSelectedCell: setSelectedCell,
    extentCell,
    onSetExtentCell: setExtentCell,
  });

  const tableMeta = useMemo(
    () => ({
      tableType,
      bodyCellSelected,
      bodyCellOnInteract,
      bodyCellOnMouseEnter,
      headerCellOnClick,
      bomRowIdxsByRowId,
    }),
    [
      tableType,
      bodyCellOnInteract,
      bodyCellOnMouseEnter,
      bodyCellSelected,
      headerCellOnClick,
      bomRowIdxsByRowId,
    ],
  );

  const resetTableState = useCallback(() => {
    setSelectedCell(null);
    setExtentCell(null);
    // FIXME: We should not have to manually do this, but it's a large risk to make rangeSelectedCells
    // a computed value at the moment.
    setRangeSelectedCells([]);
  }, [setSelectedCell, setExtentCell, setRangeSelectedCells]);

  const updateTableType = useCallback(
    (tableType: ValueOf<typeof TABLE_TYPES>) => {
      setActiveTableType(tableType);
      resetTableState();
    },
    [setActiveTableType, resetTableState],
  );

  // NOTE: currently i am using two different types of table config, one for
  // multi-level and one for the raw table, which causes some typing issues
  // Something to invest in making clearer later
  const table = useReactTable({
    data,
    columns,
    defaultColumn,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.children || [],
    initialState: {
      expanded: true,
    },
    meta: tableMeta,
  });

  const { rows } = table.getRowModel();

  const visibleRowNumbers = useMemo(() => buildVisibleRowNumbers(rows), [rows]);

  const getContentOfCell = useCallback(
    (cell: SpreadsheetSelectionCell) => {
      return getCellFromSpreadsheetCell(rows, cell)?.getValue() as string;
    },
    [rows],
  );

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const gridRef = useRef(null);

  // Close the modal and reset the reverse table rows state, and unselect the cells
  const onCloseModal = useCallback(() => {
    resetTableState();
    onCloseExtractedTableModal();
  }, [onCloseExtractedTableModal, resetTableState]);

  useClickAway(gridRef, (e) => {
    // If the click is on the table copy button, do nothing
    if (!buttonRef.current || buttonRef.current === e.target) {
      return;
    }
    // Otherwise, reset the table state
    resetTableState();
  });

  const gridOnKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // If we're focused on the grid, then fire the keydown handler
      if (document.activeElement === gridRef.current) {
        spreadsheetSelectionGridOnKeydown(
          event,
          getContentOfCell,
          visibleRowNumbers,
        );
      }
    },
    [
      getContentOfCell,
      spreadsheetSelectionGridOnKeydown,
      visibleRowNumbers,
      gridRef,
    ],
  );

  const selectedCellCss = useMemo(
    () => buildCssForSelectedCells(rows, selectedCell, extentCell, tableType),
    [tableType, extentCell, rows, selectedCell],
  );

  const headers = table.getFlatHeaders();
  const selectedHeaderCss = useMemo(
    () =>
      buildCssForHeaderCells(
        headers,
        getRowOrHeaderCellState,
        visibleRowNumbers,
      ),
    [headers, getRowOrHeaderCellState, visibleRowNumbers],
  );

  const tooltipProps = showTooltip
    ? {
        label: (
          <Flex alignItems="center" gap={0.5}>
            <CheckmarkIcon />
            Copied
          </Flex>
        ),
        isOpen: true,
      }
    : {
        label: 'Copy to clipboard',
      };

  const handleCopyClick = useCallback(() => {
    if (selectedCell) {
      // Copy selection
      const s = generateCopyPasteText(
        extentCell ? rangeSelectedCells : [selectedCell],
        getContentOfCell,
      );
      onAttemptCopy(s !== '' ? s : ' ');
    } else {
      // Copy full table
      const allCells = getCellsInRectangularRange(
        { row: 0, column: startColumn },
        { row: numRows - 1, column: columns.length - 1 },
      );
      const s = generateCopyPasteText(allCells, getContentOfCell);
      onAttemptCopy(s !== '' ? s : ' ');
    }
  }, [
    columns.length,
    extentCell,
    getContentOfCell,
    numRows,
    onAttemptCopy,
    rangeSelectedCells,
    selectedCell,
    startColumn,
  ]);

  const initialFocusRef = useRef(null);

  return (
    <>
      <Modal
        isOpen={isExtractedTableModalOpen}
        onClose={onCloseModal}
        size="xl"
        initialFocusRef={initialFocusRef}
      >
        <ModalContent
          overflow="clip"
          margin={0}
          aria-label="Extracted table"
          maxWidth={modalContentWidth}
          width={modalWidth}
          minWidth={tableMinWidth}
        >
          <ModalHeader
            display="flex"
            alignItems="center"
            gap="1rem"
            justifyContent="space-between"
            paddingRight="3.5rem"
            sx={
              shouldShowControl
                ? {
                    'button[aria-label="Close"]': {
                      top: '0.875rem',
                    },
                  }
                : {}
            }
          >
            <Flex direction="column" minWidth={0} flex={1}>
              Extracted table
              {fileLink}
            </Flex>
            <Box
              display="flex"
              flexDirection="row"
              gap={2}
              height={4}
              alignItems="center"
              flexShrink={0}
            >
              <NoBomInfoPopover showBom={showBom} rawTableData={rawTableData} />
              {tableType === TABLE_TYPES.RAW && (
                <Tooltip label="Reverse table rows" placement="top">
                  <IconButton
                    onClick={() => setReverseTableRows(!reverseTableRows)}
                  >
                    <SwapVerticalIcon />
                  </IconButton>
                </Tooltip>
              )}
              {shouldShowControl && (
                <SegmentControl value={tableType} onChange={updateTableType}>
                  <Segment value={TABLE_TYPES.BOM}>BOM</Segment>
                  <Segment value={TABLE_TYPES.RAW}>Raw</Segment>
                </SegmentControl>
              )}
              {!isLoading && (
                <Tooltip {...tooltipProps}>
                  <Button
                    ref={buttonRef}
                    variant="primary"
                    width={19}
                    height={4}
                    onClick={handleCopyClick}
                  >
                    {selectedCell ? 'Copy selection' : 'Copy table'}
                  </Button>
                </Tooltip>
              )}
            </Box>
            <ModalCloseButton ref={initialFocusRef} />
          </ModalHeader>
          <ModalBody
            maxHeight="calc(100vh - 300px)"
            display="flex"
            justifyContent="center"
            padding={0}
          >
            {isLoading ? (
              <Center>
                <Spinner />
              </Center>
            ) : hasTableDataError ? (
              <Error>
                <EmptyDrawerIcon />
                <ErrorHeadline>Unable to load table data</ErrorHeadline>
                <ErrorDescription>
                  Reach out to support at support@paperlessparts.com
                </ErrorDescription>
              </Error>
            ) : tableJsonData ? (
              <TableContainer
                flexGrow={1}
                overflow="auto"
                width="100%"
                sx={{
                  ...selectedCellCss,
                  ...selectedHeaderCss,
                }}
                border="none"
                borderRadius={0}
              >
                <Box
                  ref={gridRef}
                  onKeyDown={gridOnKeydown}
                  width="100%"
                  tabIndex={0}
                  _focus={{ outline: 'none' }}
                >
                  <TableV2>
                    <Thead
                      position="sticky"
                      top={0}
                      backgroundColor="page.plateLight"
                      // We want the sticky left row to not override the sticky header vertical scroll
                      zIndex={10}
                    >
                      {table.getHeaderGroups().map((headerGroup) => (
                        <ExtractedTableHeaderRow
                          key={headerGroup.id}
                          headerGroup={headerGroup}
                        />
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row) => (
                        <ExtractedTableRow key={row.id} row={row} />
                      ))}
                    </Tbody>
                  </TableV2>
                </Box>
              </TableContainer>
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
